import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import React, { FC, ReactNode, useCallback, useEffect, useState } from "react";

import styles from "@/components/forms/comfirmations/genericConfirmationModal.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";

type GenericConfirmationModalProps = {
  title?: string;
  message: string | ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  isOpen: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  autoCloseAfter?: number;
  disableConfirmFor?: number;
  disableCancelButton?: boolean;
};

const GenericConfirmationModal: FC<GenericConfirmationModalProps> = ({
  title,
  message,
  confirmButtonText,
  cancelButtonText,
  isOpen,
  onConfirm,
  onCancel,
  disableConfirmFor,
  disableCancelButton,
}) => {
  const [isConfirmDisabled, setIsConfirmDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (disableConfirmFor) {
      setIsConfirmDisabled(true);
      const timer = setTimeout(
        () => setIsConfirmDisabled(false),
        disableConfirmFor
      );
      return () => clearTimeout(timer);
    }
  }, [disableConfirmFor]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Enter" && onConfirm && !isConfirmDisabled) {
        event.preventDefault();
        onConfirm();
      }
    },
    [isConfirmDisabled, onConfirm]
  );

  useEffect(() => {
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    } else {
      window.removeEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleKeyDown]);

  const cancelClose = useModalDismiss(isOpen, onCancel, {
    enabled: !disableCancelButton,
  });

  return (
    <Dialog
      open={isOpen}
      scroll="paper"
      PaperProps={{ className: styles.confirmationModalWrapper }}
      {...cancelClose}
    >
      <DialogTitle className={styles.confirmationModalTitle}>
        {title}
      </DialogTitle>
      <DialogContent className={styles.confirmationModalContent}>
        {message}
      </DialogContent>
      <DialogActions
        className={
          !disableCancelButton
            ? styles.confirmationModalActionWrapper
            : styles.confirmationModalActionWrapperOneButton
        }
      >
        {!disableCancelButton && (
          <GenericButton variant="outlined" onClick={onCancel}>
            {cancelButtonText}
          </GenericButton>
        )}
        <GenericButton
          onClick={onConfirm}
          disabled={isConfirmDisabled}
          data-testid="generic-confirmation-modal-confirm-button"
        >
          {confirmButtonText}
        </GenericButton>
      </DialogActions>
    </Dialog>
  );
};

GenericConfirmationModal.displayName = "GenericConfirmationModal";

export default React.memo(GenericConfirmationModal);
